"use client";

import React from "react";

import Image from "next/image";
import { cn } from "@/lib/utils";
import { stegaClean } from "@sanity/client/stega";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { useReducedMotion, useSpring } from "framer-motion";

import Chip from "@/components/bastian-keller/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { MotionDiv, MotionP } from "@/components/global/utils/motionTags";
import { promoteLayer } from "@/components/global/utils/motionLayer";
import { EASE_OUT } from "@/components/global/utils/motionTiming";

import { PageBastianKellerSchema } from "@/sanity/pages/PageBastianKellerTemplate";

import dots from "@/public/vsl-short/dots.svg";

/*
  Trailing spring for the card — same tuning as components/career/Positions.tsx,
  which carries this interaction on the careers page. Stiff enough to read as
  attached to the pointer, damped past oscillation so it settles rather than
  wobbles.
*/
const CURSOR_SPRING = {
  stiffness: 300,
  damping: 30,
  mass: 0.5,
  restDelta: 0.5,
} as const;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), Math.max(min, max));

// the desktop card that follows the cursor over the rows is parked for now —
// flip back to true to bring it back. Rows still dim on hover either way.
const SHOW_CURSOR_CARD = false;

// every company carries its own brand colour; the green is only what an item
// with nothing picked falls back to
const FALLBACK_COLOR = "#ABF364";

type Company = PageBastianKellerSchema["companiesItems"][number];

function CompanyCard({ item, className }: { item?: Company; className?: string }) {
  // a Mux field with no video picked yet is an empty object rather than null, so
  // the playback id is what says whether there is anything to play
  const cardVideo = item?.cardVideo;
  const hasVideo = Boolean(cardVideo?.muxVideo?.playbackId);

  // stega-encoded hexes are not valid CSS colours, so the editing overlay has to
  // be stripped before either colour reaches a style attribute
  const color = stegaClean(item?.cardColor) || FALLBACK_COLOR;
  const bulletColor = stegaClean(item?.cardBulletColor) || color;

  return (
    <div
      style={{ borderTopColor: color }}
      className={cn(
        // nothing overflows this box, and a clip on a subtree that is transformed
        // every frame is another reason for the compositor to re-rasterise it
        "flex flex-col gap-[2rem] border-t-[0.375rem] bg-white px-[2.5rem] pb-[2.5rem] pt-[2.75rem] lg:gap-[1.875rem]",
        className,
      )}>
      {hasVideo && cardVideo && (
        <div className="relative h-[21.49rem] w-full overflow-hidden rounded-[0.5rem] bg-[#0A1A1D] lg:h-[26.5rem]">
          <HLSVideoPlayer
            source={cardVideo}
            autoPlay
            muted
            loop
            playsInline
            className="absolute top-0 left-0 !h-full !w-full object-cover"
          />
        </div>
      )}

      {item?.cardLogo?.src && (
        <Image
          className="!h-[5rem] !w-fit"
          quality={100}
          src={item.cardLogo}
          alt={item?.name || ""}
        />
      )}

      {/*
        Below lg the card is the whole entry, so it carries the description the
        desktop layout puts in the row beside the company name.
      */}
      {item?.description && (
        <p className="m-0 font-geist text-[2.25rem] leading-[1.55] text-black/70 lg:hidden">{item.description}</p>
      )}

      {item?.cardFeatures && item.cardFeatures.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-[1rem] p-0">
          {item.cardFeatures.map((feature, index) => (
            <li
              key={index}
              className="flex items-start gap-[1.875rem] bg-[#F3F2F1]/40 p-[1.25rem]">
              <span
                style={{ backgroundColor: bulletColor }}
                className="mt-[0.375rem] h-[1rem] w-[1rem] shrink-0"
              />
              <div className="richtext richtext-start flex-1 prose-p:font-geist prose-p:text-[1.75rem] prose-p:leading-[1.55] prose-p:text-black/70 lg:prose-p:text-[2.25rem] lg:prose-p:leading-[1.4] prose-strong:font-medium prose-strong:text-black">
                <PortableText
                  value={feature?.text}
                  components={richTextComponent}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function CompaniesSection({ data, className }: { data: PageBastianKellerSchema; className?: string }) {
  const items = data?.companiesItems;

  // null means nothing is hovered: every row reads at full strength and the card
  // stays out. Hovering one row dims the rest.
  const [active, setActive] = React.useState<number | null>(null);
  // which row's content the card is showing; it lags `active` only in that it is
  // never cleared, so the card keeps its last content while it fades out
  const [shown, setShown] = React.useState(0);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();

  const x = useSpring(0, CURSOR_SPRING);
  const y = useSpring(0, CURSOR_SPRING);

  /*
    The card rides the pointer rather than sitting where the Figma froze it, and
    is clamped to the list so it never hangs outside the section. It is centred on
    the cursor on both axes; `pointer-events-none` keeps it from stealing the
    hover from the row underneath.

    The clamp wins over the centring at the edges: within half a card of the list
    bounds the card stops travelling and the cursor walks across it instead. That
    is the point of the clamp — a card that stayed glued to the cursor there would
    hang outside the section, which `overflow-clip` would then cut in half.
  */
  const followCursor = (event: React.MouseEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const card = cardRef.current;
    const cardWidth = card?.offsetWidth ?? 0;
    const cardHeight = card?.offsetHeight ?? 0;

    const nextX = clamp(event.clientX - bounds.left - cardWidth / 2, 0, bounds.width - cardWidth);
    const nextY = clamp(event.clientY - bounds.top - cardHeight / 2, 0, bounds.height - cardHeight);

    if (reduceMotion) {
      x.jump(nextX);
      y.jump(nextY);
      return;
    }

    x.set(nextX);
    y.set(nextY);
  };

  if (!items?.length) return null;

  return (
    <section className={cn("relative bg-[#F3F2F1] overflow-clip", className)}>
      <div className="relative mx-auto w-[min(240rem,100%)] lg:h-[132.375rem]">
        <Image
          src={dots}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute top-[0.375rem] left-1/2 !w-[62.31rem] max-w-none -translate-x-1/2 select-none lg:top-0 lg:!w-[119.875rem]"
        />

        {/*
          The rule the rows sat under is inside the desktop-only list, so below lg
          the section carries its own — the mobile frame opens on it.
        */}
        <DecorationLine
          className="top-0 lg:hidden"
          orientation="horizontal"
          style="dark"
        />
        {/* ...and closes on one: 35px under the last card, with the page showing below it before Mission */}
        <DecorationLine
          className="bottom-[4.625rem] lg:hidden"
          orientation="horizontal"
          style="dark"
        />

        <DecorationLine
          className="left-[1rem] lg:left-[7.125rem]"
          orientation="vertical"
          style="dark"
        />
        <DecorationLine
          className="right-[1rem] lg:right-[7rem]"
          orientation="vertical"
          style="dark"
        />

        <div className="relative flex flex-col items-center gap-[3rem] px-[2.375rem] pt-[6.375rem] pb-[9rem] lg:gap-[8rem] lg:px-[7.375rem] lg:pt-[6.125rem] lg:pb-0">
          {data?.companiesChip?.text && (
            <Chip
              className="[&_img]:!h-[2rem] [&_img]:!w-[2rem] [&_img]:border [&_img]:border-[#202223] [&_p]:font-geistmono [&_p]:text-[#202223] [&_p]:uppercase"
              image={data?.companiesChip?.icon}
              text={data?.companiesChip?.text}
            />
          )}

          {/*
            Below lg the cards are the section: the numbered rows and the card
            that follows the cursor are both a pointer interaction, and there is
            no pointer to follow, so the mobile frame drops them and stacks the
            cards the rows were revealing.
          */}
          <div className="flex w-full flex-col gap-[3rem] lg:hidden">
            {items.map((item, index) => (
              <CompanyCard
                key={index}
                item={item}
              />
            ))}
          </div>

          <div
            className="relative hidden w-full lg:block"
            onMouseMove={SHOW_CURSOR_CARD ? followCursor : undefined}
            onMouseLeave={() => setActive(null)}>
            <DecorationLine
              className="top-0"
              orientation="horizontal"
              style="dark"
            />

            {items.map((item, index) => {
              const isDimmed = active !== null && active !== index;
              const fade = {
                duration: reduceMotion ? 0 : 0.35,
                ease: EASE_OUT,
              };

              return (
                <div
                  key={index}
                  onMouseEnter={() => {
                    setActive(index);
                    setShown(index);
                  }}
                  className="relative flex flex-col items-start gap-[2rem] py-[4rem] lg:h-[26rem] lg:flex-row lg:items-center lg:gap-0 lg:py-0">
                  {/* number tab — bottom-left corner cut, number sitting high in it */}
                  <div className="shrink-0 lg:h-full lg:w-[10.75rem]">
                    <div className="relative h-[6rem] w-[4.5rem] bg-[#D9F9B9] [clip-path:polygon(0_0,100%_0,100%_100%,1.54rem_100%,0_calc(100%-1.46rem))]">
                      <div className="absolute inset-x-0 top-[1.75rem] flex justify-center">
                        <span className="font-geistmono text-[2rem] leading-none font-medium text-black uppercase [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <MotionP
                    className="m-0 flex-1 font-funnel text-[5rem] leading-none tracking-[-0.04em] text-[#0A1A1D] lg:text-[10rem]"
                    animate={{ opacity: isDimmed ? 0.2 : 1 }}
                    initial={false}
                    transition={fade}>
                    {item?.name}
                  </MotionP>

                  {item?.description && (
                    /* 0.375 of the resting 80% lands on the 30% the design dims to */
                    <MotionP
                      className="m-0 font-geist text-[2.5rem] leading-[1.3] text-black/80 lg:w-[51.25rem]"
                      animate={{ opacity: isDimmed ? 0.375 : 1 }}
                      initial={false}
                      transition={fade}>
                      {item.description}
                    </MotionP>
                  )}

                  <DecorationLine
                    className="bottom-0"
                    orientation="horizontal"
                    style="dark"
                  />
                </div>
              );
            })}

            {/*
              Always mounted rather than swapped in on hover: the follow maths needs
              the card's measured size from the very first pointer move, and keeping
              one instance alive is what lets it travel between rows instead of
              cross-fading in place.
            */}
            {SHOW_CURSOR_CARD && (
              <MotionDiv
                /*
                  Unlike the careers card this one is mostly a photo, so without a
                  compositor layer every pointer move costs a photo-sized re-raster
                  and the spring reads as stepping rather than gliding. It moves for
                  as long as the pointer is over the list — not a brief reveal — and
                  it has its own opaque background, so promotion is worth its GPU
                  memory here and costs nothing in text rendering.
                */
                transformTemplate={reduceMotion ? undefined : promoteLayer}
                className="pointer-events-none absolute top-0 left-0 z-10 hidden w-[59.25rem] lg:block"
                style={{ x, y }}
                animate={{
                  opacity: active !== null ? 1 : 0,
                  scale: active !== null ? 1 : 0.98,
                }}
                initial={false}
                transition={{ duration: reduceMotion ? 0 : 0.3, ease: EASE_OUT }}>
                <div ref={cardRef}>
                  <CompanyCard item={items[shown]} />
                </div>
              </MotionDiv>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
